// src/Pages/Commerce/CommerceEmbeddedCheckoutPage.js
//
// Public, unauthenticated hosted checkout for the THIRD-PARTY INTEGRATION
// commerce product (app/external/commerce/ + app/commerce/routes.py's
// /api/commerce/checkout/<session_id>/<token> on the backend) — distinct from
// ProductCheckoutPage.js, which is the separate merchant-defined-product-link
// Commerce system (T2A rail, off-site Stripe/PayPal redirect).
//
// A third-party platform's own backend calls POST /thirdparty/commerce/checkout
// with its API secret key server-to-server, gets back { session_id, checkout_url },
// and redirects the customer's browser here — the secret key never reaches the
// browser. Authorization on this page is the session_id + token pair in the URL
// (an HMAC derived from the integration's key at session-creation time; see
// blitz-backend/docs/commerce/README.md §2a) — no JWT.
//
// Payment itself uses Stripe's EMBEDDED Checkout (ui_mode=embedded): the
// customer pays right here, without bouncing to checkout.stripe.com. Once
// they finish, Stripe's own return_url mechanism sends them to
// CommerceEmbeddedCheckoutCompletePage.js, which is what actually confirms
// (by polling) and shows the "you can close this" message.

import React from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Paper, Stack, CircularProgress } from "@mui/material";
import { Lock as LockIcon, ErrorOutline as ErrorIcon, Storefront as StorefrontIcon } from "@mui/icons-material";
import { alpha, keyframes } from "@mui/material/styles";
import { useQuery } from "react-query";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import client from "../../API";
import { COMMERCE_BRAND as BLITZ } from "./commerceShared.js";

// Same publishable-key lookup StripeFCButton.js already established — no new
// env var needed.
const stripePublishableKey =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_STRIPE_LIVE_PUBLISHABLE_KEY;
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

const fadeUp = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`;
const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;

function PageShell({ children, wide }) {
  return (
    // No overflow:hidden here (unlike sibling Commerce pages that clip a
    // decorative absolutely-positioned grid overlay this page doesn't have) —
    // the embedded Stripe form's height varies with how many payment methods
    // are enabled, and clipping+centering combined would crop the bottom
    // (footer) instead of letting the page scroll to fit taller content.
    <Box sx={{
      minHeight: "100vh", position: "relative",
      background: `linear-gradient(160deg, ${BLITZ.skyDeep} 0%, ${BLITZ.sky} 55%, ${alpha(BLITZ.skyLight, 0.9)} 100%)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      py: { xs: 4, md: 8 }, px: 2,
    }}>
      <Box sx={{ width: "100%", maxWidth: wide ? 560 : 440, position: "relative", zIndex: 1, animation: `${fadeIn} 0.3s ease` }}>
        {children}
      </Box>
    </Box>
  );
}

function BrandMark({ merchantName }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mb: 3 }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: 1.5, bgcolor: alpha("#fff", 0.18),
        display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(6px)",
        border: `1px solid ${alpha("#fff", 0.3)}`,
      }}>
        <StorefrontIcon sx={{ fontSize: 16, color: "white" }} />
      </Box>
      <Typography variant="subtitle2" sx={{ color: "white", fontWeight: 800, letterSpacing: "0.02em" }}>
        {merchantName || "Blitz Commerce"}
      </Typography>
    </Stack>
  );
}

function LoadingState() {
  return (
    <PageShell>
      <BrandMark />
      <Paper elevation={0} sx={{ borderRadius: 4, p: 4, textAlign: "center", animation: `${fadeUp} 0.25s ease` }}>
        <CircularProgress size={32} thickness={4} sx={{ color: BLITZ.sky, mb: 2 }} />
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
          Loading checkout…
        </Typography>
      </Paper>
    </PageShell>
  );
}

function ProblemState({ message }) {
  return (
    <PageShell>
      <BrandMark />
      <Paper elevation={0} sx={{ borderRadius: 4, p: 4, textAlign: "center", animation: `${fadeUp} 0.25s ease` }}>
        <Box sx={{
          width: 72, height: 72, borderRadius: "50%", bgcolor: "#fef2f2", color: "#dc2626",
          display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2.5,
        }}>
          <ErrorIcon sx={{ fontSize: 40 }} />
        </Box>
        <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
          Checkout unavailable
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {message || "This checkout link is no longer valid. Ask the store to send you a new one."}
        </Typography>
      </Paper>
    </PageShell>
  );
}

export default function CommerceEmbeddedCheckoutPage() {
  const { sessionId, token } = useParams();

  const { data: session, isLoading, isError, error } = useQuery(
    ["commerce", "checkout-session", sessionId, token],
    () => client.commerce.getCheckoutSession(sessionId, token),
    { retry: false },
  );

  // Lazily creates the Stripe embedded Checkout Session server-side (so it
  // can't expire before the customer ever sees it) — only once we know the
  // session exists and is still payable. Cached on the (sessionId, token)
  // query key, so re-renders don't re-create it.
  const embedQuery = useQuery(
    ["commerce", "checkout-embed", sessionId, token],
    () => client.commerce.startEmbeddedCheckout(sessionId, token),
    {
      retry: false,
      enabled: !!session && session.status === "awaiting_payment" && !!stripePromise,
    },
  );

  if (isLoading) return <LoadingState />;
  if (isError || !session) {
    return <ProblemState message={error?.response?.data?.error || error?.message} />;
  }
  if (!stripePromise) {
    return <ProblemState message="Payments are temporarily unavailable. Please try again shortly." />;
  }
  if (session.status === "paid") {
    return <ProblemState message="This order has already been paid." />;
  }
  if (session.status !== "awaiting_payment") {
    return <ProblemState message="This checkout is no longer available." />;
  }
  if (!embedQuery.data?.client_secret) {
    if (embedQuery.isError) {
      return (
        <ProblemState
          message={
            embedQuery.error?.response?.data?.error ||
            embedQuery.error?.message ||
            "Couldn't start checkout — try again."
          }
        />
      );
    }
    return <LoadingState />;
  }

  return (
    <PageShell wide>
      <BrandMark merchantName={session.integration_name} />
      {/* No padding here — Stripe's embedded UI already frames its own
          content (order summary + form), so an outer padded card just
          doubles up the framing. Price/order-ref aren't repeated here
          either since Stripe's own summary already shows them prominently.
          Deliberately NO overflow:hidden on this Paper (or any ancestor) —
          Stripe's own embedded-checkout integration notes warn that
          overflow:hidden on a container ABOVE <EmbeddedCheckout> can break
          its internal auto-resize (the iframe's height is pushed via
          postMessage after mount, as content/payment-method options load),
          which reads as content being cramped/cut off rather than a clean
          visual clip. Corners are square here as a result — acceptable
          trade-off since we don't control the iframe's own content anyway. */}
      <Paper elevation={0} sx={{ borderRadius: 4, animation: `${fadeUp} 0.25s ease` }}>
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={{ clientSecret: embedQuery.data.client_secret }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </Paper>
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} sx={{ mt: 2.5, opacity: 0.85 }}>
        <LockIcon sx={{ fontSize: 13, color: "white" }} />
        <Typography variant="caption" sx={{ color: "white", fontSize: 11 }}>
          Secured by Blitz
        </Typography>
      </Stack>
    </PageShell>
  );
}
